import {
    DatakeyMetadata,
    DatakeyAttributes,
    SearchDatakeysRequest,
    SearchDatakeysResponse,
    GetDatakeyRequest,
    GetDatakeyResponse,
    CreateDatakeyRequest,
    CreateDatakeyResponse,
    CreateDatakeyMultiMarketplaceRequest,
    CreateDatakeyMultiMarketplaceResponse,
    UpdateDatakeyRequest,
    UpdateDatakeyResponse,
    DeleteDatakeyRequest,
    DeleteDatakeyResponse
} from 'src/model/DatakeyMetadata';
import ApiUtil, { HTTP_METHOD, ApiError } from './ApiUtil';
import { API } from 'aws-amplify';

class DatakeyMetadataApiUtil {
    private static async callWithQueryParams(
        method: 'POST' | 'PUT' | 'DELETE', 
        resource: string, 
        queryParams: { [key: string]: string }, 
        body?: any
    ): Promise<any> {
        const queryString = new URLSearchParams({
            ...queryParams,
            locale: 'en-US'
        }).toString();
        
        const url = `/api/${resource}?${queryString}`;
        
        const headers = {
            'anti-csrftoken-a2z': '',
            'content-type': 'application/json; charset=UTF-8'
        };

        const config = {
            headers: headers,
            response: true,
            ...(body && { body: body })
        };

        try {
            let response;
            switch (method) {
                case 'POST':
                    response = await API.post("FeeTechPortalLambda", url, config);
                    break;
                case 'PUT':
                    response = await API.put("FeeTechPortalLambda", url, config);
                    break;
                case 'DELETE':
                    response = await API.del("FeeTechPortalLambda", url, config);
                    break;
            }
            return response.data;
        } catch (error: any) {
            const statusCode = error.response?.status;
            
            switch (statusCode) {
                case 400:
                    throw new ApiError(400, this.getErrorMessage(error, "Bad request"));
                case 401:
                    throw new ApiError(401, "Unauthorized request");
                case 403:
                    throw new ApiError(403, "Forbidden request");
                case 404:
                    throw new ApiError(404, this.getErrorMessage(error, "Target item not found"));
                case 409:
                    throw new ApiError(409, this.getErrorMessage(error, "Target item already exists"));
                case 412:
                    throw new ApiError(412, "Target item was modified by another process. Please try again.");
                case 428:
                    throw new ApiError(428, "Target item was modified by another process. Please try again.");
                case 429:
                    throw new ApiError(429, "Throttled request");
                default:
                    throw new ApiError(500, "Failed to fetch data from API");
            }
        }
    }

    private static async postWithQueryParams(resource: string, queryParams: { [key: string]: string }, body: any): Promise<any> {
        return this.callWithQueryParams('POST', resource, queryParams, body);
    }

    private static async putWithQueryParams(resource: string, queryParams: { [key: string]: string }, body: any): Promise<any> {
        return this.callWithQueryParams('PUT', resource, queryParams, body);
    }

    private static async deleteWithQueryParams(resource: string, queryParams: { [key: string]: string }): Promise<any> {
        return this.callWithQueryParams('DELETE', resource, queryParams);
    }

    private static getErrorMessage(error: any, defaultErrorMessage: string): string {
        return error.response?.data?.message || error.message || defaultErrorMessage;
    }

    static async getDatakey(request: GetDatakeyRequest): Promise<GetDatakeyResponse> {
        const resource = `datakey-metadata/item/${encodeURIComponent(request.datakeyName)}`;
        
        const params: { [key: string]: any } = {
            publicFeeType: request.publicFeeType,
            marketplace: request.marketplace
        };
        
        try {
            const response = await ApiUtil.callApiWithTimeout(resource, params, 30000, HTTP_METHOD.GET);
            return response as GetDatakeyResponse;
        } catch (error: any) {
            console.error('API Error in getDatakey:', error);
            if (error instanceof ApiError) {
                throw new Error(`API Error ${error.apiErrorCode}: ${error.message}`);
            }
            throw new Error(`Failed to get datakey: ${error.message || 'Unknown error'}`);
        }
    }

    static async searchDatakeys(request: SearchDatakeysRequest): Promise<SearchDatakeysResponse> {
        const resource = `datakey-metadata/search`;
        
        const params: { [key: string]: any } = {};
        if (request.feeType) {
            params.publicFeeType = request.feeType;
        }
        if (request.datakeyName) {
            params.datakeyName = request.datakeyName;
        }
        if (request.marketplace) {
            params.marketplace = request.marketplace;
        }
        if (request.limit) {
            params.limit = request.limit;
        }
        if (request.nextToken) {
            params.nextToken = request.nextToken;
        }
        
        try {
            const response = await ApiUtil.callApiWithTimeout(resource, params, 30000, HTTP_METHOD.GET);
            return response as SearchDatakeysResponse;
        } catch (error: any) {
            console.error('API Error in searchDatakeys:', error);
            if (error instanceof ApiError) {
                throw new Error(`API Error ${error.apiErrorCode}: ${error.message}`);
            }
            throw new Error(`Failed to search datakeys: ${error.message || 'Unknown error'}`);
        }
    }

    static async createDatakey(request: CreateDatakeyRequest): Promise<CreateDatakeyResponse> {
        const resource = `datakey-metadata/item/${encodeURIComponent(request.datakeyName)}`;
        
        const queryParams = {
            publicFeeType: request.publicFeeType,
            marketplace: request.marketplace
        };
        
        const body = {
            attributes: request.attributes
        };
        
        try {
            const response = await DatakeyMetadataApiUtil.postWithQueryParams(resource, queryParams, body);
            return response as CreateDatakeyResponse;
        } catch (error: any) {
            console.error('API Error in createDatakey:', error);
            if (error instanceof ApiError) {
                throw new Error(`API Error ${error.apiErrorCode}: ${error.message}`);
            }
            throw new Error(`Failed to create datakey: ${error.message || 'Unknown error'}`);
        }
    }

    static async createDatakeyMultiMarketplace(request: CreateDatakeyMultiMarketplaceRequest): Promise<CreateDatakeyMultiMarketplaceResponse> {
        const resource = `datakey-metadata/bulk/${encodeURIComponent(request.datakeyName)}`;
        
        const queryParams = {
            publicFeeType: request.publicFeeType
        };
        
        const body = {
            marketplaces: request.marketplaces,
            attributes: request.attributes
        };
        
        try {
            const response = await DatakeyMetadataApiUtil.postWithQueryParams(resource, queryParams, body);
            return response as CreateDatakeyMultiMarketplaceResponse;
        } catch (error: any) {
            console.error('API Error in createDatakeyMultiMarketplace:', error);
            if (error instanceof ApiError) {
                throw new Error(`API Error ${error.apiErrorCode}: ${error.message}`);
            }
            throw new Error(`Failed to create datakey in multiple marketplaces: ${error.message || 'Unknown error'}`);
        }
    }

    static async updateDatakey(request: UpdateDatakeyRequest): Promise<UpdateDatakeyResponse> {
        const resource = `datakey-metadata/item/${encodeURIComponent(request.datakeyName)}`;
        
        const queryParams = {
            publicFeeType: request.publicFeeType,
            marketplace: request.marketplace
        };
        
        const body = {
            attributes: request.attributes
        };
        
        try {
            const response = await DatakeyMetadataApiUtil.putWithQueryParams(resource, queryParams, body);
            return response as UpdateDatakeyResponse;
        } catch (error: any) {
            console.error('API Error in updateDatakey:', error);
            if (error instanceof ApiError) {
                throw new Error(`API Error ${error.apiErrorCode}: ${error.message}`);
            }
            throw new Error(`Failed to update datakey: ${error.message || 'Unknown error'}`);
        }
    }

    static async deleteDatakey(request: DeleteDatakeyRequest): Promise<DeleteDatakeyResponse> {
        const resource = `datakey-metadata/item/${encodeURIComponent(request.datakeyName)}`;
        
        const queryParams = {
            publicFeeType: request.publicFeeType,
            marketplace: request.marketplace
        };
        
        try {
            const response = await DatakeyMetadataApiUtil.deleteWithQueryParams(resource, queryParams);
            return response as DeleteDatakeyResponse;
        } catch (error: any) {
            console.error('API Error in deleteDatakey:', error);
            if (error instanceof ApiError) {
                throw new Error(`API Error ${error.apiErrorCode}: ${error.message}`);
            }
            throw new Error(`Failed to delete datakey: ${error.message || 'Unknown error'}`);
        }
    }

    static getUniqueFeeTypes(datakeys: DatakeyMetadata[]): string[] {
        const feeTypes = new Set(datakeys.map(dk => dk.publicFeeType));
        return Array.from(feeTypes).sort();
    }

    static getUniqueMarketplaces(datakeys: DatakeyMetadata[]): string[] {
        const marketplaces = new Set(datakeys.map(dk => dk.marketplace));
        return Array.from(marketplaces).sort();
    }

    static validateDatakeyName(name: string): string | null {
        if (!name || !name.trim()) {
            return 'Datakey name is required';
        }
        
        if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(name)) {
            return 'Datakey name must start with a letter and contain only letters, numbers, and underscores';
        }
        
        return null;
    }

    static validateSearchFilters(filters: SearchDatakeysRequest): string | null {
        const { feeType, datakeyName, marketplace } = filters;
        
        // Check if marketplace is provided without fee type or datakey name
        if (marketplace && !feeType && !datakeyName) {
            return 'Marketplace cannot be used alone. Please provide either Fee Type or Datakey Name.';
        }
        
        // Check if datakey name + marketplace is provided without fee type (backend doesn't support this combination)
        if (datakeyName && marketplace && !feeType) {
            return 'Datakey Name + Marketplace combination requires Fee Type. Please provide Fee Type as well.';
        }
        
        // At least one field must be provided
        if (!feeType && !datakeyName && !marketplace) {
            return 'Please provide at least one search criteria: Fee Type, Datakey Name, or Marketplace.';
        }
        
        return null;
    }

    static createDefaultAttributes(): DatakeyAttributes {
        return {
            type: 'string',
            displayName: '',
            definition: '',
            excludeFromExplanation: false,
            valueOptions: []
        };
    }
}

export default DatakeyMetadataApiUtil;
